import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { useItemsNotInGroup, useAddItemToGroup, MyItem } from '@/hooks/useMyItems';
import { Search, FolderPlus, Package } from 'lucide-react';
import { T, RADIUS, SHADOW } from '@/lib/tokens';

interface AddItemsToGroupDialogProps {
  groupId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 *
 * @param root0
 * @param root0.groupId
 * @param root0.open
 * @param root0.onOpenChange
 */
export function AddItemsToGroupDialog({ groupId, open, onOpenChange }: AddItemsToGroupDialogProps) {
  const { data: availableItems, isLoading } = useItemsNotInGroup(groupId);
  const addItemToGroup = useAddItemToGroup();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  const categorizedItems = useMemo(() => {
    const filtered =
      availableItems?.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(q) ||
          item.brand?.toLowerCase().includes(q) ||
          item.category?.toLowerCase().includes(q)
        );
      }) || [];

    const byCategory = new Map<string, MyItem[]>();
    for (const item of filtered) {
      const cat = item.category || 'Uncategorized';
      const list = byCategory.get(cat) || [];
      list.push(item);
      byCategory.set(cat, list);
    }
    return [...byCategory.entries()].sort((a, b) => {
      if (a[0] === 'Uncategorized') return 1;
      if (b[0] === 'Uncategorized') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [availableItems, searchQuery]);

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // All-or-nothing category toggle: if every item in the category is already
  // selected, clicking deselects them all; otherwise it selects all of them.
  const toggleCategory = (category: string) => {
    const catItems = categorizedItems.find(([cat]) => cat === category)?.[1] || [];
    const catIds = catItems.map((i) => i.id);
    const allSelected = catIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        catIds.forEach((id) => next.delete(id));
      } else {
        catIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedIds.size === 0) return;
    setIsAdding(true);
    try {
      // Sequential loop: items are added one at a time so a single failure
      // surfaces immediately and stops the loop (the `catch` below handles it).
      // The toast on success will report how many were actually added.
      for (const itemId of selectedIds) {
        await addItemToGroup.mutateAsync({ itemId, groupId });
      }
      toast.success(`${selectedIds.size} item${selectedIds.size > 1 ? 's' : ''} added to group!`);
      setSelectedIds(new Set());
      setSearchQuery('');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to add items', { description: (error as Error).message });
    }
    setIsAdding(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedIds(new Set());
      setSearchQuery('');
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-5 w-5" />
            Add Items to Group
          </DialogTitle>
          <DialogDescription>
            Select items from your inventory to add to this group.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-8">Loading items...</p>
        ) : !availableItems || availableItems.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            All your items are already in this group, or you have no items yet.
          </p>
        ) : (
          <div className="space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Items list */}
            <ScrollArea className="h-[350px] rounded-md border p-3">
              {categorizedItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No items match your search.
                </p>
              ) : (
                <div className="space-y-4">
                  {categorizedItems.map(([category, items]) => {
                    const catIds = items.map((i) => i.id);
                    const allSelected = catIds.every((id) => selectedIds.has(id));
                    const someSelected = catIds.some((id) => selectedIds.has(id));

                    return (
                      <div key={category}>
                        {/* Category header with "Add All" */}
                        <button
                          type="button"
                          onClick={() => toggleCategory(category)}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium hover:bg-accent transition-colors"
                        >
                          <Checkbox
                            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                            onCheckedChange={() => toggleCategory(category)}
                            className="pointer-events-none"
                          />
                          <span>{category}</span>
                          <span
                            style={{
                              marginLeft: 'auto',
                              fontSize: 11,
                              fontWeight: 600,
                              padding: '2px 7px',
                              borderRadius: RADIUS.dot,
                              background: T.surfaceSubtle,
                              color: T.textMuted,
                              border: `1px solid ${T.border}`,
                            }}
                          >
                            {items.length}
                          </span>
                        </button>
                        <div style={{ height: 1, background: T.border, margin: '4px 0' }} />

                        {/* Individual items */}
                        <div className="space-y-0.5 ml-2">
                          {items.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => toggleItem(item.id)}
                              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors"
                            >
                              <Checkbox
                                checked={selectedIds.has(item.id)}
                                onCheckedChange={() => toggleItem(item.id)}
                                className="pointer-events-none"
                              />
                              <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <span className="truncate text-left">{item.name}</span>
                              {item.brand && (
                                <span className="text-xs text-muted-foreground ml-auto shrink-0">
                                  {item.brand}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Footer */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{selectedIds.size} selected</p>
              <button
                onClick={handleAdd}
                disabled={selectedIds.size === 0 || isAdding}
                style={{
                  padding: '10px 18px',
                  borderRadius: RADIUS.md,
                  background: selectedIds.size === 0 || isAdding ? T.surfaceSubtle : T.accent,
                  color: selectedIds.size === 0 || isAdding ? T.textMuted : T.white,
                  border: selectedIds.size === 0 || isAdding ? `1px solid ${T.border}` : 'none',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: selectedIds.size === 0 || isAdding ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: selectedIds.size === 0 || isAdding ? 'none' : SHADOW.btn.accent,
                }}
              >
                {isAdding
                  ? 'Adding...'
                  : `Add ${selectedIds.size > 0 ? selectedIds.size : ''} Item${selectedIds.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
